import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BaseSource, ChatHelperContext } from "contexts/ChatHelper";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { SubscriptionContext } from "contexts/Subscription";
import {
  KeyboardEvent,
  MutableRefObject,
  MouseEvent as ReactMouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { UpgradeDialogSource } from "schemas/dashboard";

import { AI_DRAFT_CREDITS_TOOLTIP } from "constants/influencer";
import {
  FormState,
  PersonalizeToneOption,
  TONE_OPTIONS,
} from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog/schema";

import ChatMessage from "./ChatMessage";
import MissingCaseStudyDialog from "./MissingCaseStudyDialog";
import TypingLoader from "./TypingLoader";
import styles from "./styles";

const REF_INDEX = 5;

type PersonalizationKey =
  | "referenceInstagram"
  | "referenceProduct"
  | "mentionCaseStudy"
  | "contentIdea"
  | "audienceAlignment";

const PERSONALIZATION_CHIPS: { key: PersonalizationKey; label: string }[] = [
  { key: "referenceInstagram", label: "Reference Instagram" },
  { key: "referenceProduct", label: "Reference product" },
  { key: "mentionCaseStudy", label: "Mention case study" },
  { key: "contentIdea", label: "Add content idea" },
  { key: "audienceAlignment", label: "Audience alignment" },
];

const ChatHelper = () => {
  const { quickSendBrandId } = useContext(QuickSendDrawerContext);
  const { outreachMessages, bentoBrand } = useContext(QuickSendContext);
  const { discoverTab, setDiscoverTab } = useContext(DiscoverViewContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const {
    chatMessages,
    messageLoading,
    messageLimit,
    fetchChatMessages,
    isTyping,
    runPersonalize,
  } = useContext(ChatHelperContext);

  const draft = outreachMessages[0]?.body;
  const hasDraft = Boolean(draft) && quickSendBrandId > 0;

  const remainingCredits = subscription?.remainingChatMessages ?? 0;
  const isPaidUser = Boolean(currentOrg?.isPaidUser);
  const outOfCredits = remainingCredits <= 0;

  const [freeformText, setFreeformText] = useState("");
  const [caseStudyDialogOpen, setCaseStudyDialogOpen] = useState(false);
  const [baseSource, setBaseSource] = useState<BaseSource>("editor");
  const [editingMenuAnchor, setEditingMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [optionsCollapsed, setOptionsCollapsed] = useState(false);

  const hasPreviousAiReply = chatMessages.some(
    (m) =>
      m.role === "assistant" &&
      !m.streaming &&
      !!m.message &&
      !!m.countsTowardQuota,
  );

  useEffect(() => {
    if (!hasPreviousAiReply && baseSource === "previous_ai" && !isTyping) {
      setBaseSource("editor");
    }
  }, [hasPreviousAiReply, baseSource, quickSendBrandId, isTyping]);

  const activeTemplateId = outreachMessages[0]?.templateId;
  useEffect(() => {
    setBaseSource("editor");
  }, [quickSendBrandId, activeTemplateId]);

  const prefilledForBrandRef = useRef<number | null>(null);
  useEffect(() => {
    if (!quickSendBrandId || quickSendBrandId < 0) return;
    if (!bentoBrand?.normalizedName) return;
    if (bentoBrand.id !== quickSendBrandId) return;
    if (prefilledForBrandRef.current === quickSendBrandId) return;
    prefilledForBrandRef.current = quickSendBrandId;
    setFreeformText(`Customize a pitch for ${bentoBrand.normalizedName}`);
  }, [quickSendBrandId, bentoBrand?.id, bentoBrand?.normalizedName]);

  useEffect(() => {
    if (!quickSendBrandId || quickSendBrandId < 0) {
      setFreeformText("");
      return;
    }
    if (bentoBrand?.id !== quickSendBrandId) {
      setFreeformText("");
    }
  }, [quickSendBrandId, bentoBrand?.id]);

  const newestMessageRef = useRef<HTMLDivElement | null>(null);
  const personalizationRowDrag = useDragScroll();
  const toneRowDrag = useDragScroll();

  const scrollContainerRef: MutableRefObject<HTMLDivElement | null> =
    useRef(null);
  const [topRef, isTopVisible] = useInView({
    root: scrollContainerRef.current,
    threshold: 1,
  });

  useEffect(() => {
    if (discoverTab !== DiscoverTabView.CHAT) return;
    if (newestMessageRef.current) {
      newestMessageRef.current.scrollIntoView({ block: "start" });
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverTab, chatMessages?.length, chatMessages[0]?.message, isTyping]);

  useEffect(() => {
    if (
      (isTopVisible || chatMessages.length === 0) &&
      !messageLimit &&
      !messageLoading
    ) {
      fetchChatMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTopVisible,
    messageLimit,
    messageLoading,
    currentOrg?.id,
    quickSendBrandId,
  ]);

  const getRef = (idx: number) => {
    const isTopAnchor =
      (chatMessages.length > REF_INDEX
        ? idx === chatMessages.length - REF_INDEX
        : idx === chatMessages.length - 1) &&
      !messageLimit &&
      !messageLoading;
    return (node: HTMLDivElement | null) => {
      if (idx === 0) newestMessageRef.current = node;
      if (isTopAnchor) topRef(node);
    };
  };

  const closeDialog = () => setDiscoverTab(null);

  const handleBuyMore = () => {
    setUpgradeDialogSource(
      UpgradeDialogSource.hits_personalized_ai_messages_limit,
    );
  };

  const guardCreditsOrRun = (override: Partial<FormState>) => {
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    setFreeformText("");
    setBaseSource("previous_ai");
    runPersonalize(override, { baseSource });
  };

  const handlePersonalizationChip = (key: PersonalizationKey) => {
    if (!hasDraft || isTyping) return;
    if (outOfCredits) {
      handleBuyMore();
      return;
    }

    if (key === "mentionCaseStudy") {
      const existingCaseStudy = (profile?.caseStudy ?? "").trim();
      if (!existingCaseStudy) {
        setCaseStudyDialogOpen(true);
        return;
      }
      setFreeformText("");
      setBaseSource("previous_ai");
      runPersonalize(
        {
          mentionCaseStudy: true,
          caseStudyText: existingCaseStudy,
        },
        { baseSource },
      );
      return;
    }

    if (key === "referenceProduct") {
      setFreeformText("");
      setBaseSource("previous_ai");
      runPersonalize(
        {
          referenceProduct: true,
          productRef: { kind: "ai_find" },
        },
        { baseSource },
      );
      return;
    }

    if (key === "contentIdea") {
      setFreeformText("");
      setBaseSource("previous_ai");
      runPersonalize(
        {
          contentIdea: true,
          contentIdeaState: { kind: "ai_generate" },
        },
        { baseSource },
      );
      return;
    }

    if (key === "referenceInstagram") {
      guardCreditsOrRun({ referenceInstagram: true });
      return;
    }

    if (key === "audienceAlignment") {
      guardCreditsOrRun({ audienceAlignment: true });
    }
  };

  const handleToneChip = (tone: PersonalizeToneOption) => {
    if (!hasDraft || isTyping) return;
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    setFreeformText("");
    setBaseSource("previous_ai");
    runPersonalize({ tone }, { baseSource });
  };

  const handleSubmitCaseStudy = (caseStudyText: string) => {
    setCaseStudyDialogOpen(false);
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    setFreeformText("");
    setBaseSource("previous_ai");
    runPersonalize(
      {
        mentionCaseStudy: true,
        caseStudyText,
      },
      { baseSource },
    );
  };

  const handleSendFreeform = () => {
    const trimmed = freeformText.trim();
    if (!trimmed || !hasDraft || isTyping) return;
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    setFreeformText("");
    setBaseSource("previous_ai");
    runPersonalize({ freeform: trimmed }, { baseSource });
  };

  const handleFreeformKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendFreeform();
    }
  };

  const sendDisabled =
    !hasDraft || isTyping || freeformText.trim().length === 0;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%", flex: 1 }}
    >
      {/* Header + credits banner */}
      <Box sx={styles.header}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="i"
              className="fa-solid fa-wand-magic-sparkles"
              sx={styles.headerIcon}
            />
            <Typography sx={styles.headerTitle}>AI Writer</Typography>
          </Box>
          <IconButton sx={styles.close} onClick={closeDialog}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Grid>
        <Box sx={styles.creditsBanner}>
          <Typography sx={styles.creditsText}>
            {remainingCredits} credit{remainingCredits === 1 ? "" : "s"} left
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    color: "black",
                    backgroundColor: "white",
                    p: 1,
                    boxShadow: 1,
                  },
                },
              }}
              title={AI_DRAFT_CREDITS_TOOLTIP}
              enterTouchDelay={0}
            >
              <Box
                component="i"
                className="fa-solid fa-circle-info"
                sx={styles.creditsInfoIcon}
              />
            </Tooltip>
          </Typography>
          {!isPaidUser && (
            <Button
              size="small"
              disableElevation
              onClick={handleBuyMore}
              sx={styles.buyMoreButton}
            >
              Buy more
            </Button>
          )}
        </Box>
      </Box>

      {/* Chat thread */}
      <Box ref={scrollContainerRef} sx={styles.body}>
        {messageLoading && chatMessages.length === 0 && (
          <Box sx={styles.loadingStack}>
            <Skeleton
              animation="wave"
              variant="rounded"
              height={36}
              sx={styles.loadingBubbleUser}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              height={120}
              sx={styles.loadingBubbleAssistant}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              height={48}
              sx={styles.loadingBubbleUser}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              height={88}
              sx={styles.loadingBubbleAssistant}
            />
          </Box>
        )}
        <Grid container direction="column-reverse">
          {isTyping && !chatMessages[0]?.streaming && (
            <Grid item xs={12} sx={styles.typing}>
              <TypingLoader title="Thinking" />
            </Grid>
          )}

          {chatMessages?.length === 0 && !messageLoading && (
            <Box sx={styles.emptyState}>
              <Box
                component="i"
                className="fa-solid fa-wand-magic-sparkles"
                sx={styles.emptyStateIcon}
              />
              <Typography sx={styles.emptyStateTitle}>
                {hasDraft ? "No messages yet" : "No draft open"}
              </Typography>
              <Typography sx={styles.emptyStateBody}>
                {hasDraft
                  ? `Send a message or pick a personalization option to customize your pitch${
                      bentoBrand?.normalizedName
                        ? ` to ${bentoBrand.normalizedName}`
                        : ""
                    }.`
                  : "Open an outreach draft and I'll help you customize the pitch here."}
              </Typography>
            </Box>
          )}
          {chatMessages.map((chatMessage, idx) => (
            <ChatMessage
              key={idx}
              chatMessage={chatMessage}
              ref={getRef(idx)}
            />
          ))}
        </Grid>
      </Box>

      {/* Quick-action chips */}
      {hasDraft && (
        <Box sx={styles.quickActions}>
          <Tooltip
            title={optionsCollapsed ? "Show options" : "Hide options"}
            placement="left"
            enterDelay={400}
          >
            <IconButton
              size="small"
              onClick={() => setOptionsCollapsed((v) => !v)}
              sx={styles.collapseToggle}
              aria-label={
                optionsCollapsed
                  ? "Show personalization options"
                  : "Hide personalization options"
              }
            >
              <Box
                component="i"
                className={
                  optionsCollapsed
                    ? "fa-solid fa-chevron-up"
                    : "fa-solid fa-chevron-down"
                }
                sx={styles.collapseToggleIcon}
              />
            </IconButton>
          </Tooltip>
          <Collapse in={!optionsCollapsed} unmountOnExit={false}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography sx={styles.quickActionsLabel}>
                Add personalization:
              </Typography>
              <Box
                ref={personalizationRowDrag.ref}
                sx={styles.chipRow}
                {...personalizationRowDrag.handlers}
              >
                {PERSONALIZATION_CHIPS.map(({ key, label }) => (
                  <Button
                    key={key}
                    size="small"
                    disableElevation
                    disabled={isTyping}
                    onClick={(e) => {
                      if (personalizationRowDrag.consumeClickIfDragged(e))
                        return;
                      handlePersonalizationChip(key);
                    }}
                    sx={styles.chip}
                  >
                    {label}
                  </Button>
                ))}
              </Box>

              <Typography sx={styles.quickActionsLabel}>
                Adjust tone/length:
              </Typography>
              <Box
                ref={toneRowDrag.ref}
                sx={styles.chipRow}
                {...toneRowDrag.handlers}
              >
                {TONE_OPTIONS.map(({ id, label, emoji }) => (
                  <Button
                    key={id}
                    size="small"
                    disableElevation
                    disabled={isTyping}
                    onClick={(e) => {
                      if (toneRowDrag.consumeClickIfDragged(e)) return;
                      handleToneChip(id);
                    }}
                    sx={styles.chip}
                  >
                    <Box component="span" sx={{ mr: 0.5 }}>
                      {emoji}
                    </Box>
                    {label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Freeform composer */}
      <Box sx={styles.composer}>
        {hasDraft && (
          <Box sx={styles.editingSourceRow}>
            <Typography component="span" sx={styles.editingSourceLabel}>
              Editing:
            </Typography>
            <Chip
              size="small"
              clickable
              onClick={(e) =>
                setEditingMenuAnchor(e.currentTarget as HTMLElement)
              }
              label={
                baseSource === "previous_ai"
                  ? "AI's last reply"
                  : "current draft"
              }
              deleteIcon={
                <Box component="i" className="fa-solid fa-chevron-down" />
              }
              onDelete={(e) =>
                setEditingMenuAnchor(
                  (e.currentTarget as HTMLElement).closest(
                    ".MuiChip-root",
                  ) as HTMLElement,
                )
              }
              sx={[
                styles.editingSourceChip,
                Boolean(editingMenuAnchor) && styles.editingSourceChipOpen,
              ]}
            />
            <Menu
              anchorEl={editingMenuAnchor}
              open={Boolean(editingMenuAnchor)}
              onClose={() => setEditingMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <MenuItem
                selected={baseSource === "editor"}
                onClick={() => {
                  setBaseSource("editor");
                  setEditingMenuAnchor(null);
                }}
              >
                Current draft
              </MenuItem>
              <MenuItem
                disabled={!hasPreviousAiReply}
                selected={baseSource === "previous_ai"}
                onClick={() => {
                  setBaseSource("previous_ai");
                  setEditingMenuAnchor(null);
                }}
              >
                AI&apos;s last reply
              </MenuItem>
            </Menu>
          </Box>
        )}
        <Box sx={styles.composerRow}>
          <TextField
            multiline
            minRows={2}
            maxRows={5}
            placeholder={
              hasDraft
                ? "Ask AI to write or edit..."
                : "Open a draft to chat with AI"
            }
            value={freeformText}
            onChange={(e) => setFreeformText(e.target.value)}
            onKeyDown={handleFreeformKeyDown}
            disabled={!hasDraft || isTyping}
            sx={styles.composerInput}
          />
          <IconButton
            onClick={handleSendFreeform}
            disabled={sendDisabled}
            sx={styles.sendButton}
          >
            <Box
              component="i"
              className="fa-solid fa-paper-plane-top"
              sx={styles.sendIcon}
            />
          </IconButton>
        </Box>
      </Box>

      <MissingCaseStudyDialog
        open={caseStudyDialogOpen}
        onClose={() => setCaseStudyDialogOpen(false)}
        onSubmit={handleSubmitCaseStudy}
      />
    </Box>
  );
};

export default ChatHelper;

const DRAG_THRESHOLD_PX = 5;

function useDragScroll() {
  const ref = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    didDrag: false,
  });

  const onMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    stateRef.current = {
      isDown: true,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      didDrag: false,
    };
  };

  const onMouseMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !stateRef.current.isDown) return;
    const dx = e.clientX - stateRef.current.startX;
    if (!stateRef.current.didDrag && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      stateRef.current.didDrag = true;
    }
    if (stateRef.current.didDrag) {
      e.preventDefault();
      el.scrollLeft = stateRef.current.startScrollLeft - dx;
    }
  };

  const stop = () => {
    stateRef.current.isDown = false;
  };

  // Ate by the chip click handler when a drag completed; prevents the chip
  // from firing its action after a drag-to-scroll gesture.
  const consumeClickIfDragged = (e: ReactMouseEvent<HTMLElement>): boolean => {
    if (stateRef.current.didDrag) {
      e.preventDefault();
      e.stopPropagation();
      stateRef.current.didDrag = false;
      return true;
    }
    return false;
  };

  return {
    ref,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp: stop,
      onMouseLeave: stop,
    },
    consumeClickIfDragged,
  };
}
